import { type MicroCMSListResponse, type MicroCMSQueries } from 'microcms-js-sdk';

import { useSentry } from '@/composables/utils/useSentry';
import { useMicroCMSClient } from '@/wabunka-src/common/composables/query/useMicroCMSClient';
import { useFilteredFeaturedListQuery } from '@/wabunka-src/common/features/Featured/composables/query/useGetFilteredFeaturedListQuery';
import { buildMinimumFeaturedListViewModel, type MinimumFeaturedListViewModel, type MinimumFeaturedResponse } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';

// 1回あたりの取得件数は10件固定（これ以上にするとmicroCMSサーバーのtimeoutエラー）
const LIMIT_PER_REQUEST = 10;
const FEATURED_FIELDS = ['id', 'titleEn', 'titleJa', 'backgroundWideMedias', 'backgroundTallMedias', 'type', 'destinationTags', 'themeTags', 'itineraryTagSettings'];

// area / category は廃止予定の `/areas/{id}/` `/themes/{id}/` にしか遷移先がないため引かない（JCAT-6198）
const ALL_FEATURED_TYPES = ['itinerary', 'theme', 'destination'] as const;
export type FeaturedQueryType = (typeof ALL_FEATURED_TYPES)[number];

/**
 * limitsとtypesからuseAsyncData用のキーを生成する
 * 同じlimits+typesで呼び出された場合、Nuxtのキャッシュ機能により重複リクエストを防止する
 */
const buildCacheKey = (limits?: { itinerary?: number; theme?: number; destination?: number }, types?: readonly FeaturedQueryType[]) => {
  const hasLimits = limits && Object.keys(limits).length > 0;
  const sortedLimits = hasLimits ? JSON.stringify(limits, Object.keys(limits).sort()) : '';
  const sortedTypes = types && types.length > 0 ? JSON.stringify([...types].sort()) : '';
  const suffix = [sortedLimits, sortedTypes].filter(Boolean).join('-');
  return `featured-list${suffix ? `-${suffix}` : ''}`;
};

/**
 * Wabunka TopページのFeaturedセクションに表示するコンテンツ（Micro CMS）を取得する Composable
 *
 * @param options.itinerary - 旅程の最大取得件数
 * @param options.theme - テーマの最大取得件数
 * @param options.destination - デスティネーションの最大取得件数
 * @param options.types - 取得するタイプを絞り込む配列（オプション、未指定時は全タイプ取得）
 */
export const useFeaturedListQuery = async (options?: { itinerary?: number; theme?: number; destination?: number; types?: FeaturedQueryType[] }) => {
  const { types: targetTypes, ...limits } = options ?? {};
  const activeTypes = targetTypes ?? ALL_FEATURED_TYPES;

  const { captureWabunkaException } = useSentry();

  // useRequestFetch() を内部で呼ぶため、await より前に取得する
  const { getList } = useMicroCMSClient();

  const fetchFeaturedList = (queries: MicroCMSQueries) => {
    return getList<MinimumFeaturedResponse>({ endpoint: 'en-featured', queries });
  };

  /**
   * 指定されたタイプのFeaturedアイテムを取得する
   * @param type - 取得するタイプ ('itinerary' | 'theme' | 'destination')
   * @param limit - 最大取得件数（指定がない場合は全件取得）
   */
  const fetchFeaturedItemsByType = async (type: FeaturedQueryType, limit?: number) => {
    // 初回リクエストで総件数を取得
    const firstData = await fetchFeaturedList({
      fields: FEATURED_FIELDS,
      filters: `type[contains]${type}`,
      limit: limit !== undefined ? Math.min(limit, LIMIT_PER_REQUEST) : LIMIT_PER_REQUEST,
      offset: 0,
    });

    if (!firstData) {
      throw new Error(`Failed to fetch ${type} featured items data.`);
    }

    const totalCount = firstData.totalCount;
    const targetCount = limit !== undefined ? Math.min(limit, totalCount) : totalCount;

    // 既に必要な件数を取得済みの場合は追加リクエスト不要
    if (firstData.contents.length >= targetCount) {
      return {
        contents: firstData.contents.slice(0, targetCount),
        totalCount,
        offset: 0,
        limit: targetCount,
      } as MicroCMSListResponse<MinimumFeaturedResponse>;
    }

    // 必要なリクエスト数を計算
    const totalRequests = Math.ceil(targetCount / LIMIT_PER_REQUEST);

    // 並列でリクエストを作成（最初のリクエストは既に実行済み）
    const promises: Promise<MicroCMSListResponse<MinimumFeaturedResponse>>[] = [];

    for (let i = 1; i < totalRequests; i++) {
      const offset = i * LIMIT_PER_REQUEST;
      const remainingCount = targetCount - offset;
      const requestLimit = Math.min(remainingCount, LIMIT_PER_REQUEST);

      promises.push(
        fetchFeaturedList({
          fields: FEATURED_FIELDS,
          filters: `type[contains]${type}`,
          limit: requestLimit,
          offset,
        }),
      );
    }

    // 全てのリクエストを並列実行
    // Promise.allは配列の順序を保持するため、resultsは必ずpromises配列と同じ順序になる
    const results = await Promise.all(promises);

    // 順序を保持してデータを結合
    const allContents: MinimumFeaturedResponse[] = [...firstData.contents];

    // Promise.allの結果は入力順を保持しているため、offset順（10, 20, 30...）でデータが結合される
    for (const result of results) {
      if (!result) {
        throw new Error(`Failed to fetch ${type} featured items data.`);
      }
      allContents.push(...result.contents);
    }

    // 必要な件数だけ返す
    return {
      contents: allContents.slice(0, targetCount),
      totalCount,
      offset: 0,
      limit: targetCount,
    } as MicroCMSListResponse<MinimumFeaturedResponse>;
  };

  const emptyResult: MicroCMSListResponse<MinimumFeaturedResponse> = { contents: [], totalCount: 0, offset: 0, limit: 0 };

  const fetchOrSkip = (type: FeaturedQueryType, limit?: number) => {
    return activeTypes.includes(type) ? fetchFeaturedItemsByType(type, limit) : Promise.resolve(emptyResult);
  };

  const fetchAllFeaturedItems = async () => {
    // タイプごとに並列でリクエスト（activeTypesに含まれないタイプはスキップ）
    const [itineraryResult, themeResult, destinationResult] = await Promise.all([fetchOrSkip('itinerary', limits?.itinerary), fetchOrSkip('theme', limits?.theme), fetchOrSkip('destination', limits?.destination)]);

    // 全ての結果を結合
    const allContents: MinimumFeaturedResponse[] = [...itineraryResult.contents, ...themeResult.contents, ...destinationResult.contents];

    return {
      contents: allContents,
      totalCount: itineraryResult.totalCount + themeResult.totalCount + destinationResult.totalCount,
      offset: 0,
      limit: allContents.length,
    } as MicroCMSListResponse<MinimumFeaturedResponse>;
  };

  // useAsyncDataでラップして重複リクエストを防止
  // 同じlimits+typesで呼び出された場合、キャッシュされた結果を返す
  const cacheKey = buildCacheKey(limits, activeTypes);
  const { data } = await useAsyncData(cacheKey, async () => {
    try {
      return await fetchAllFeaturedItems();
    } catch (e) {
      captureWabunkaException(e as Error);
      throw e;
    }
  });

  const featuredList = computed<MinimumFeaturedListViewModel>(() => buildMinimumFeaturedListViewModel(data.value?.contents) || []);

  const { itineraryList, themeList, destinationList } = useFilteredFeaturedListQuery(featuredList);

  return { itineraryList, themeList, destinationList, featuredList };
};
